<template>
    <div class="contacts-container">
        <div :style="wrapperStyles" class="contacts-wrapper">
            <div id="preloader" v-if="load" class="preloader"></div>
            <header class="contacts-header">
                <p class="title">Contacts</p>
                <p class="contacts-count">{{ numOfContacts }}</p>
                <button
                    v-if="screen.isTablet.value"
                    @click="toggleSortMode"
                    class="sort-mobile"
                >
                    <img :src="URLS.icons.mobileSort" alt="mobile-sort" />
                </button>
                <button
                    v-if="!screen.isTablet.value"
                    @click="toggleAddNewContact"
                    class="add-new-btn"
                >
                    Add new
                </button>
                <button v-else @click="toggleAddNewContact" class="add-new-btn-mobile">
                    <img :src="URLS.icons.mobileAdd" alt="mobile-add-icon" />
                </button>
            </header>
            <div class="divider"></div>
            <main class="contacts-main">
                <div class="contacts-main-header">
                    <div v-if="!screen.isTablet.value" class="sort-by">
                        <p class="label">Sort by:</p>
                        <p @click="toggleSortMode" class="sort-by-mode">{{ sortByMode }}</p>
                    </div>
                    <div v-if="!screen.isTablet.value" class="view-options">
                        <p class="label">View:</p>
                        <p @click="toggleViewMode" class="view-options-mode">{{ viewMode }}</p>
                    </div>
                    <input
                        v-model="searchFilter"
                        :style="searchFilterStyles"
                        class="contacts-search-input"
                        type="text"
                        placeholder="Search contacts"
                    />
                </div>
                <div class="contacts-main-content">
                    <div v-if="showContactViews" class="contacts-main-left">
                        <div v-if="showContactsBoard" class="board-view">
                            <ContactsBoardView
                                :user="userInfo"
                                :contacts="renderContacts"
                                @filter-keyword="handleFilterKeyword"
                                @remove-contact="handleRemoveContact"
                            />
                        </div>
                        <div v-else-if="showContactsList" class="list-view">
                            <ContactsListView
                                :user="userInfo"
                                :contacts="renderContacts"
                                @filter-keyword="handleFilterKeyword"
                                @remove-contact="handleRemoveContact"
                            />
                        </div>
                    </div>
                    <div v-if="!screen.isTablet.value" class="contacts-main-right">
                        <!-- @TODO: once features are available uncomment -->
                        <!-- <div class="contact-lists-card-wrapper"> -->
                        <!--     <ContactListsCard /> -->
                        <!-- </div> -->
                        <div
                            v-if="showSuggestedContacts"
                            class="interesting-contacts-card-wrapper"
                        >
                            <InterestingContacts
                                :contacts="renderSuggestedContacts"
                                @added-contact="handleAddedSuggestedContact"
                            />
                        </div>
                    </div>
                </div>
            </main>
            <footer
                v-if="!screen.isTablet.value && contacts.length >= 7"
                class="contacts-footer"
            >
                <button @click="handlePrevPage" class="prev-paginate">
                    <img :src="URLS.icons.prev" alt="prev-icon" />
                    Previous
                </button>
                <div class="pages-paginate">
                    <span
                        v-for="(page, indx) in pages"
                        :key="indx"
                        :style="paginateStyle(page)"
                        class="page"
                    >
                        {{ page }}
                    </span>
                </div>
                <button @click="handleNextPage" class="next-paginate">
                    Next
                    <img :src="URLS.icons.next" alt="next-icon" />
                </button>
            </footer>
            <div v-if="showAddNewContact" class="add-new-contact-wrapper">
                <div @click="toggleAddNewContact" class="overlay"></div>
                <AddNewContactModal
                    :contacts="suggestedContacts"
                    @added-contact="handleAddedSuggestedContact"
                    @close-modal="toggleAddNewContact"
                />
            </div>
        </div>
    </div>
</template>
<script>
    import axios from 'axios'
    import { notification } from '@/helpers/notification'
    import useCheckScreen from '@/composables/useCheckScreen.js'
    import ContactsBoardView from './parts/ContactsBoardView.vue'
    import ContactsListView from './parts/ContactsListView.vue'
    //import ContactListsCard from './parts/ContactListsCard.vue'
    import InterestingContacts from './parts/InterestingContacts.vue'
    import AddNewContactModal from './parts/AddNewContactModal.vue'

    const URLS = {
        xhr: {
            fetch: {
                user: '/api/profile/v1/settings/user',
                venueContacts: '/api/profile/contacts/venue/list',
                artistContacts: '/api/profile/contacts/artist/list',
                suggestedContacts: '/api/profile/contacts/suggested/list'
            }
        },
        icons: {
            next: '/images/icons/next.svg',
            prev: '/images/icons/prev.svg',
            mobileAdd: '/images/icons/mobile-add-contact.svg',
            mobileSort: '/images/icons/mobile-sort.svg'
        }
    }
    export default {
        name: 'Contacts',
        components: {
            ContactsBoardView,
            ContactsListView,
            //ContactListsCard,
            InterestingContacts,
            AddNewContactModal
        },
        data: () => ({
            load: false,
            showAddNewContact: false,
            searchFilter: '',
            contactsPerPage: 16,
            filteredContactsCount: 0,
            pageNumber: 1,
            viewMode: 'Board',
            sortByMode: 'A-Z',
            userInfo: {},
            contacts: [],
            suggestedContacts: []
        }),
        computed: {
            URLS: () => URLS,
            wrapperStyles: (self) =>
                self.screen.isTablet.value ? { 'min-width': '320px', padding: '0px' } : {},
            searchFilterStyles: (self) =>
                self.screen.isTablet.value ? { 'margin-right': 'auto' } : {},
            contactsCount: (self) => self.contacts.length,
            numOfContacts: (self) => {
                if (self.searchFilter) {
                    return self.filteredContactsCount
                } else {
                    return self.contactsCount
                }
            },
            numOfPages: (self) => {
                if (self.searchFilter) {
                    return Math.ceil(self.filteredContactsCount / self.contactsPerPage)
                } else {
                    return Math.ceil(self.contactsCount / self.contactsPerPage)
                }
            },
            pages: (self) => {
                let pages = []
                let numOfPages = self.numOfPages
                let offset = self.numOfPages - 2

                for (let i = 0; i < numOfPages; i++) {
                    if (
                        numOfPages > offset &&
                        i > 2 &&
                        i < numOfPages - 3 &&
                        !pages.includes('...')
                    ) {
                        pages.push('...')
                    } else {
                        pages.push((i + 1).toString())
                    }
                }

                return pages
            },
            renderContacts: (self) => {
                if (self.searchFilter) {
                    let filteredContacts = self.contacts.filter(
                        (c) => self.filterByName(c) || self.filterByKeyword(c)
                    )

                    self.appendPageNumber(filteredContacts)

                    if (!self.screen.isTablet.value) {
                        let contacts = filteredContacts.filter(
                            (c) => c.page === self.pageNumber
                        )
                        self.filteredContactsCount = contacts.length

                        return contacts
                    } else {
                        self.filteredContactsCount = filteredContacts.length
                        return filteredContacts
                    }
                }

                self.appendPageNumber(self.contacts)
                if (self.screen.isTablet.value) {
                    return self.contacts.filter((c) => c.id)
                }
                return self.contacts.filter((c) => c.page === self.pageNumber)
            },
            showContactViews: (self) => self.renderContacts.length,
            showContactsBoard: (self) =>
                self.viewMode === 'Board' && !self.screen.isTablet.value,
            showContactsList: (self) => self.viewMode === 'List' || self.screen.isTablet.value,
            paginateStyle: (self) => (page) => {
                if (page === self.pageNumber.toString()) {
                    return {
                        background: '#CDEB09',
                        color: 'black'
                    }
                } else if (
                    page === '...' &&
                    !self.pages.find((p) => p === self.pageNumber.toString())
                ) {
                    return {
                        background: '#CDEB09',
                        color: 'black'
                    }
                } else {
                    return {}
                }
            },
            showSuggestedContacts: (self) =>
                (self.renderSuggestedContacts.length && self.userInfo.isVenue) ||
                (self.renderSuggestedContacts.length &&
                    !self.userInfo.isVenue &&
                    !self.userInfo.isArtist),
            renderSuggestedContacts: (self) => {
                if (self.suggestedContacts && self.suggestedContacts.length) {
                    return self.suggestedContacts
                        .filter((c) => c.role === 'artist')
                        .slice(0, 4)
                } else {
                    return []
                }
            },
            filterByName: (self) => (c) =>
                c.venue_name
                    ? c.venue_name.toLowerCase().includes(self.searchFilter.toLowerCase())
                    : c.name.toLowerCase().includes(self.searchFilter.toLowerCase()) ||
                      c.band_name
                    ? c.band_name.toLowerCase().includes(self.searchFilter.toLowerCase())
                    : c.name.toLowerCase().includes(self.searchFilter.toLowerCase()),
            filterByKeyword: (self) => (c) => {
                return (
                    c.keyword &&
                    c.keyword.toLowerCase().includes(self.searchFilter.toLowerCase())
                )
            }
        },
        watch: {
            searchFilter: function () {
                this.resetPageNumber()
            }
        },
        setup() {
            const { isTablet } = useCheckScreen()

            return {
                screen: {
                    isTablet
                }
            }
        },
        async created() {
            this.userInfo = await this.fetchUserInfo()
            this.suggestedContacts = await this.fetchSuggestedContacts()

            if (this.userInfo.isArtist) {
                this.contacts = this.contacts.concat(await this.fetchArtistContacts())
            }
            if (this.userInfo.isVenue) {
                this.contacts = this.contacts.concat(await this.fetchVenueContacts())
            }
            if (!this.userInfo.isArtist && !this.userInfo.isVenue) {
                this.contacts = this.contacts.concat(await this.fetchArtistContacts())
            }
            this.initContacts()
        },
        methods: {
            initContacts() {
                this.appendId(this.contacts)
                this.contacts = this.sortContacts()
                this.appendPageNumber(this.contacts)
            },
            async fetchSuggestedContacts() {
                try {
                    this.load = true
                    const resp = await axios.get(this.URLS.xhr.fetch.suggestedContacts)

                    return resp.data.data
                } catch (e) {
                    notification('error', e)
                } finally {
                    this.load = false
                }
            },
            async fetchUserInfo() {
                try {
                    this.load = true
                    const resp = await axios.get(this.URLS.xhr.fetch.user)

                    return resp.data.data[0]
                } catch (e) {
                    notification('error', e)
                } finally {
                    this.load = false
                }
            },
            async fetchVenueContacts() {
                try {
                    this.load = true
                    const resp = await axios.get(this.URLS.xhr.fetch.venueContacts)

                    return resp.data.data
                } catch (e) {
                    notification('error', e)
                } finally {
                    this.load = false
                }
            },
            async fetchArtistContacts() {
                try {
                    this.load = true
                    const resp = await axios.get(this.URLS.xhr.fetch.artistContacts)

                    return resp.data.data
                } catch (e) {
                    notification('error', e)
                } finally {
                    this.load = false
                }
            },
            sortContacts() {
                return this.contacts.sort((a, b) => {
                    const contactAName =
                        a.role === 'venue' ? a.venue_name ?? a.name : a.band_name ?? a.name
                    const contactBName =
                        b.role === 'venue' ? b.venue_name ?? b.name : b.band_name ?? b.name

                    return contactAName.localeCompare(contactBName)
                })
            },
            reverseSortContacts() {
                return this.contacts.sort((a, b) => {
                    const contactAName =
                        a.role === 'venue' ? a.venue_name ?? a.name : a.band_name ?? a.name
                    const contactBName =
                        b.role === 'venue' ? b.venue_name ?? b.name : b.band_name ?? b.name

                    return contactBName.localeCompare(contactAName)
                })
            },
            appendPageNumber(contacts) {
                for (let i = 0; i < contacts.length; i++) {
                    contacts[i] = {
                        ...contacts[i],
                        page: Math.ceil((i + 1) / 16)
                    }
                }
            },
            appendId(contacts) {
                for (let i = 0; i < contacts.length; i++) {
                    contacts[i] = {
                        ...contacts[i],
                        id: i
                    }
                }
            },
            toggleViewMode() {
                this.resetPageNumber()
                this.viewMode = this.viewMode === 'Board' ? 'List' : 'Board'
            },
            toggleSortMode() {
                this.sortByMode = this.sortByMode === 'A-Z' ? 'Z-A' : 'A-Z'
                this.resetPageNumber()
                this.contacts =
                    this.sortByMode === 'A-Z'
                        ? this.sortContacts()
                        : this.reverseSortContacts()
            },
            toggleAddNewContact() {
                this.showAddNewContact = !this.showAddNewContact
            },
            resetPageNumber() {
                this.pageNumber = 1
            },
            handleNextPage() {
                if (this.pageNumber < this.numOfPages) {
                    this.pageNumber++
                }
            },
            handlePrevPage() {
                if (this.pageNumber > 1) {
                    this.pageNumber--
                }
            },
            handleFilterKeyword(keyword) {
                this.searchFilter = keyword
            },
            handleRemoveContact(contact) {
                let targetContact = this.contacts.find((c) => c.id === contact.id)
                let indx = this.contacts.indexOf(targetContact)
                this.contacts.splice(indx, 1)
            },
            handleAddedSuggestedContact(contact) {
                let targetContact = this.suggestedContacts.find((c) => c.id === contact.id)
                let indx = this.suggestedContacts.indexOf(targetContact)
                this.suggestedContacts.splice(indx, 1)

                switch (contact.role) {
                    case 'venue':
                        contact.venue_id = contact.id
                        break
                    case 'artist':
                        contact.artist_id = contact.id
                }
                this.contacts.push(contact)
                this.initContacts()
            }
        }
    }
</script>
<style lang="scss" scoped>
    .contacts-container {
        display: flex;
        flex-direction: row;
        justify-content: center;
        width: 100%;

        .contacts-wrapper {
            width: clamp(300px, 100%, 1168px);
            padding: 85px 60px 65px 60px;
            background: #121212;
            display: flex;
            flex-direction: column;

            p {
                margin-bottom: 0px;
            }

            button {
                border: none;
            }

            .contacts-header {
                display: flex;
                flex-direction: row;
                align-items: center;

                .title {
                    color: #eff0f2;
                    font-family: Instrument Sans;
                    font-size: 44px;
                    font-style: normal;
                    font-weight: 500;
                    line-height: 110%;
                    letter-spacing: -1.76px;
                }

                .contacts-count {
                    color: #8b8b8b;
                    font-family: Instrument Sans;
                    font-size: 38px;
                    font-style: normal;
                    font-weight: 500;
                    line-height: 110%;
                    letter-spacing: -0.76px;
                    margin-left: 4px;
                }

                .add-new-btn {
                    padding: 11px 30px 12px 30px;
                    border-radius: 100px;
                    background: #cdeb09;
                    min-width: 133px;
                    margin-left: auto;
                    color: black;
                    font-family: Instrument Sans;
                    font-size: 18px;
                    font-style: normal;
                    font-weight: 650;
                    line-height: 120%;
                }

                .add-new-btn-mobile {
                    background: none;
                    margin-left: auto;
                }

                .sort-mobile {
                    background: none;
                }
            }

            .divider {
                width: 100%;
                height: 1px;
                background: #646464;
                margin-top: 25px;
                margin-bottom: 37px;
            }

            .contacts-main {
                display: flex;
                flex-direction: column;
                align-items: center;

                .contacts-main-header {
                    display: flex;
                    flex-direction: row;
                    justify-content: flex-start;
                    gap: 5px;
                    width: 100%;

                    .sort-by,
                    .view-options {
                        display: flex;
                        flex-direction: row;
                        align-items: center;
                        gap: 8px;

                        .label,
                        .sort-by-mode,
                        .view-options-mode {
                            color: #dedede;
                            font-family: Instrument Sans;
                            font-size: 18px;
                            font-style: normal;
                            font-weight: 650;
                            line-height: 120%;
                        }

                        .sort-by-mode,
                        .view-options-mode {
                            cursor: pointer;
                        }
                    }

                    .view-options {
                        margin-left: 20px;
                    }

                    .contacts-search-input {
                        margin-left: auto;
                        border-radius: 12px;
                        padding: 12px 16px 16px 16px;
                        border: 1px solid #646464;
                        background: #121212;
                        overflow: hidden;
                        color: #dedede;
                        font-family: Instrument Sans;
                        font-size: 16px;
                        font-style: normal;
                        font-weight: 550;
                        line-height: 125%;
                        background-image: url(/images/icons/search_icon_grey.svg);
                        background-repeat: no-repeat;
                        background-position: 95%;
                        padding-right: 45px;
                        min-width: 280px;
                    }

                    .contacts-search-input::placeholder {
                        color: #8b8b8b;
                        font-family: Instrument Sans;
                        font-size: 16px;
                        font-style: normal;
                        font-weight: 550;
                        line-height: 125%;
                    }
                }

                .contacts-main-content {
                    display: flex;
                    flex-direction: row;
                    justify-content: center;
                    gap: 20px;
                    width: 100%;
                    margin-top: 24px;

                    .contacts-main-right {
                        margin-left: auto;
                        display: flex;
                        flex-direction: column;
                        gap: 20px;
                    }
                }
            }
            .contacts-footer {
                margin-top: 65px;
                display: flex;
                flex-direction: row;
                justify-content: space-between;
                align-items: center;
                max-width: 692px;

                .prev-paginate,
                .next-paginate {
                    padding: 8px 24px 8px 20px;
                    display: flex;
                    flex-direction: row;
                    align-items: center;
                    gap: 8px;
                    background: none;
                    color: #acacac;
                    font-family: Instrument Sans;
                    font-size: 16px;
                    font-style: normal;
                    font-weight: 550;
                    line-height: 125%;

                    img {
                        width: 24px;
                        height: 24px;
                    }
                }
                .pages-paginate {
                    display: flex;
                    flex-direction: row;
                    justify-content: center;
                    align-items: center;

                    .page {
                        border-radius: 8px;
                        padding: 8px;
                        height: 36px;
                        width: 36px;
                        color: #8b8b8b;
                        font-family: Instrument Sans;
                        font-size: 16px;
                        font-style: normal;
                        font-weight: 550;
                        line-height: 125%;
                        text-align: center;
                    }
                }
            }
            .add-new-contact-wrapper {
                display: flex;
                justify-content: center;

                .overlay {
                    position: fixed;
                    width: 100vw;
                    height: 100vh;
                    background: black;
                    opacity: 0.9;
                    top: 0;
                    left: 0;
                    z-index: 1;
                }
            }
        }
    }
</style>
